import type {IFilterQuery} from '@shared';
import type {IParentAds, IParentAdsPageableResponseData} from '@shared/ads-modules';
import type {
    IDeleteCustomerFavoriteAds,
    ICreateCustomerFavoriteAds,
    ICustomerFavoriteAds,
    IIsFavorite,
} from '@shared/customer';
import {FAVORITE_ITEM_KEY} from '~/utils/defs';
import {useUserStore} from './user';
import {useParentAdsStore} from './parent-ads';

const ENDPOINT_PREFIX = 'customer-favorite-ads';

export const useCustomerFavoriteStore = defineStore('customer-favorite-ads', () => {
    const apiClient = useApiClient();
    const favorites = ref<IParentAds[]>([]);
    const {setItem, getItem} = useLocalStorage();
    const userStore = useUserStore();
    const parentAdsStore = useParentAdsStore();

    const filter = async(payload?: IFilterQuery) => {
        if (!payload) payload = {page: 1, limit: 1000};
        const result = await apiClient.post<IFilterQuery, IParentAdsPageableResponseData>(ENDPOINT_PREFIX + '/filter', payload);
        if (result.success) {
            const data = result.data?.data as IParentAds[];
            favorites.value = data;
        }
        return result;
    };

    const add = async(payload: ICreateCustomerFavoriteAds) => {
        if (!userStore.getCustomerId()) {
            const items = getItem(FAVORITE_ITEM_KEY, []) as IParentAds[];
            if (!items.some(v => v.id == payload.adsId)) {
                const ads = await parentAdsStore.getById(payload.adsId);
                const item = ads.data as IParentAds;
                items.push(item);
                favorites.value.push(item);
            }
            setItem(FAVORITE_ITEM_KEY, items);
        } else {
            const result = await apiClient.post<ICreateCustomerFavoriteAds, ICreateCustomerFavoriteAds>(ENDPOINT_PREFIX + '/add', payload);
            if (result.success) {
                const ads = await parentAdsStore.getById(payload.adsId);
                favorites.value.push(ads.data as IParentAds);
            }
        }
    };

    const remove = async(payload: IDeleteCustomerFavoriteAds) => {
        if (!userStore.getCustomerId()) {
            let items = getItem(FAVORITE_ITEM_KEY, []) as IParentAds[];
            const item = items.find(v => v.id == payload.adsId);
            if (item) {
                items = items.filter(v => v.id != payload.adsId);
                favorites.value = favorites.value.filter(v => v.id != payload.adsId);
            }
            setItem(FAVORITE_ITEM_KEY, items);
        } else {
            const result = await apiClient.post<IDeleteCustomerFavoriteAds, IDeleteCustomerFavoriteAds>(ENDPOINT_PREFIX + '/remove', payload);
            if (result.success) favorites.value = favorites.value.filter(v => v.id != payload.adsId);
        }
    };

    const isFavorite = async(itemId: string) => {
        const customer = await userStore.getCustomerId();
        if (customer) {
            const result = await apiClient.post<IIsFavorite, Boolean>(ENDPOINT_PREFIX + '/isFavorite', {
                id: itemId,
                customerId: customer,
            });
            if (result.success) return result.data;
        } else {
            const items = getItem(FAVORITE_ITEM_KEY, []) as IParentAds[];
            return items.length && items.some(v => v.id == itemId);
        }
    };


    const initFavorites = async() => {
        if (userStore.getCustomerId()) await filter();
        else favorites.value = getItem(FAVORITE_ITEM_KEY, []) as IParentAds[];
    };

    const clearFavorites = () => {
        favorites.value = [];
    };

    return {
        remove,
        add,
        filter,
        favorites,
        isFavorite,
        initFavorites,
        clearFavorites,
    };
});
